import { isDemoSession } from "./api";

export const STUDENT_PRACTICE_PROGRESS_STORAGE_KEY = "elume:student:practice-quiz-progress:v1";
export const DEMO_STUDENT_PRACTICE_PROGRESS_STORAGE_KEY = "elume:demo:student:practice-quiz-progress:v1";

export type StudentPracticeQuizQuestionSnapshot = {
  id: number;
  prompt: string;
  choices: string[];
  position?: number;
};

export type StudentPracticeQuizSnapshot = {
  id: number;
  classId: number;
  scope?: "class" | "global";
  title: string;
  className?: string;
  questions: StudentPracticeQuizQuestionSnapshot[];
};

export type StudentPracticeQuizResult = {
  totalQuestions: number;
  correctCount: number;
  percentage: number;
  questions: Array<{ questionId: number; selectedIndex: number; correct: boolean; correctIndex: number; explanation?: string }>;
};

export type StudentPracticeQuizProgressRecord =
  | { state: "in_progress"; answers: Record<string, number>; currentQuestionIndex: number; updatedAt: number }
  | { state: "completed"; quiz: StudentPracticeQuizSnapshot; result: StudentPracticeQuizResult; completedAt: number };

type StoredRecords = Record<string, StudentPracticeQuizProgressRecord>;

function progressKey(classId: number, quizId: number, scope: "class" | "global" = "class") {
  return scope === "global" ? `global:quiz:${quizId}` : `class:${classId}:quiz:${quizId}`;
}

export function getStudentPracticeQuizStatus(classId: number, quizId: number, scope: "class" | "global" = "class"): "New" | "In progress" | "Completed" {
  const state = getStudentPracticeQuizRecord(classId, quizId, scope)?.state;
  return state === "completed" ? "Completed" : state === "in_progress" ? "In progress" : "New";
}

function readRecords(): StoredRecords {
  try {
    const raw = (isDemoSession() ? sessionStorage : localStorage).getItem(
      isDemoSession() ? DEMO_STUDENT_PRACTICE_PROGRESS_STORAGE_KEY : STUDENT_PRACTICE_PROGRESS_STORAGE_KEY
    );
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed as StoredRecords : {};
  } catch {
    return {};
  }
}

function writeRecords(records: StoredRecords) {
  try {
    const storage = isDemoSession() ? sessionStorage : localStorage;
    storage.setItem(
      isDemoSession() ? DEMO_STUDENT_PRACTICE_PROGRESS_STORAGE_KEY : STUDENT_PRACTICE_PROGRESS_STORAGE_KEY,
      JSON.stringify(records)
    );
  } catch {
    // Browser storage is only a convenience for this stateless quiz flow.
  }
}

export function getStudentPracticeQuizRecord(classId: number, quizId: number, scope: "class" | "global" = "class"): StudentPracticeQuizProgressRecord | null {
  const records = readRecords();
  const record = records[progressKey(classId, quizId, scope)] || (scope === "class" ? records[`${classId}:${quizId}`] : null);
  if (!record || typeof record !== "object") return null;
  if (record.state === "in_progress" && record.answers && typeof record.answers === "object" && Number.isFinite(record.currentQuestionIndex)) return record;
  if (record.state === "completed" && record.quiz && record.result) return record;
  return null;
}

export function saveStudentPracticeQuizProgress(classId: number, quizId: number, answers: Record<number, number>, currentQuestionIndex: number, scope: "class" | "global" = "class") {
  const records = readRecords();
  records[progressKey(classId, quizId, scope)] = {
    state: "in_progress",
    answers: Object.fromEntries(Object.entries(answers).filter(([, value]) => Number.isInteger(value))),
    currentQuestionIndex: Math.max(0, Math.floor(currentQuestionIndex) || 0),
    updatedAt: Date.now(),
  };
  writeRecords(records);
}

export function saveStudentPracticeQuizCompleted(quiz: StudentPracticeQuizSnapshot, result: StudentPracticeQuizResult) {
  const records = readRecords();
  records[progressKey(quiz.classId, quiz.id, quiz.scope || "class")] = { state: "completed", quiz, result, completedAt: Date.now() };
  writeRecords(records);
}

export function clearStudentPracticeQuizRecord(classId: number, quizId: number, scope: "class" | "global" = "class") {
  const records = readRecords();
  delete records[progressKey(classId, quizId, scope)];
  if (scope === "class") delete records[`${classId}:${quizId}`];
  writeRecords(records);
}

export function studentPracticeQuizProgressKey(classId: number, quizId: number, scope: "class" | "global" = "class") {
  return progressKey(classId, quizId, scope);
}
